import { debounce } from 'vue-debounce';
import { mapActions } from 'vuex';
import { capturePreview } from '@/components/Editor/Shared/previewCapture';
import { DIGITAL_PRICE, FRAMES, SIZES } from '@/components/Editor/Shared/productOptions';

// Order state shared by the product editors. The editor provides `basePrice`, `selectedOptions`
// (with frame, size, title, message, hasDigital) and a `preview-window` ref.
export default {
    data() {
        return {
            frameOptions: FRAMES,
            sizeOptions: SIZES,
            quantity: 1,
            price: 0,
            previewImage: null,
            mutatedOptions: null,
            isPreviewCapturing: false,
            isDropdownOpen: false,
            isProductExist: false,
            randomId: null,
            hash: null,
            // wood editors set this so cart extras and stored ids don't collide with the posters
            editorType: null,
        };
    },
    computed: {
        selectedFrame() {
            return (
                FRAMES.find((frame) => frame.id === this.selectedOptions.frame.id)?.preview ||
                this.selectedOptions.frame?.preview
            );
        },
        savedProductId() {
            return this.$route.hash ? this.$route.hash.split('#')[2] : undefined;
        },
        cartExtras() {
            return {
                ...this.selectedOptions,
                hash: this.$route.hash ? this.savedProductId : this.hash,
                editor: this.editorType,
            };
        },
    },
    watch: {
        quantity() {
            this.checkOptions(this.selectedOptions);
        },
    },
    created() {
        this.price = this.basePrice;
        this.debouncedCheckOptions = debounce((options) => {
            this.checkOptions(options);
            this.revealPreview();
        }, 300);
    },
    methods: {
        ...mapActions(['storeCustomizedProductDetails']),
        // Options saved for the product in the url hash (e.g. back from the cart), or null.
        loadSavedProduct() {
            const customizedProducts = JSON.parse(localStorage.getItem('customizedProducts'));
            if (customizedProducts) customizedProducts.forEach((product) => this.storeCustomizedProductDetails(product));

            const saved = this.savedProductId
                ? this.$store.state.customized_products.find(
                      (options) => String(options.id) === String(this.savedProductId)
                  )
                : null;
            if (saved?.editor && this.editorType && saved.editor !== this.editorType) {
                this.isProductExist = false;
                return null;
            }
            this.isProductExist = Boolean(saved);
            return saved || null;
        },
        storeProduct() {
            const { preview, ...options } = this.$cloneDeep(this.selectedOptions);
            this.storeCustomizedProductDetails({
                ...options,
                editor: this.editorType,
                id: this.savedProductId || String(this.hash || this.randomId),
            });
        },
        createRandomId() {
            if (!this.hash) {
                this.randomId = Math.floor(Math.random() * 1000) + 1;
                this.hash = this.editorType ? `${this.editorType}-${this.randomId}` : this.randomId;
            }
        },
        checkOptions(currentOptions) {
            if (!this.isProductExist) {
                this.createRandomId();
            } else {
                this.randomId = parseInt(this.$route.hash.substring(1));
            }

            const frame = FRAMES.find((option) => option.id === currentOptions.frame?.id);
            const size = SIZES.find((option) => option.id === currentOptions.size?.id);
            this.selectedOptions.frame.name = frame?.name;
            this.selectedOptions.frame.price = frame?.price;
            this.selectedOptions.size.name = size?.name;
            this.selectedOptions.size.price = size?.price;
            this.selectedOptions.hasDigital.price = this.selectedOptions.hasDigital.active ? DIGITAL_PRICE : 0;

            this.mutatedOptions = { ...this.selectedOptions };
            const optionsPrice = Object.values(this.selectedOptions).reduce(
                (sum, option) => sum + ((option && option.price) || 0),
                0
            );
            this.price = (this.basePrice + optionsPrice) * this.quantity;
        },
        // Below lg the form sits under the preview, so bring the preview back into view after a change.
        revealPreview() {
            const preview = this.$refs['preview-window'];
            if (!preview || !window.matchMedia('(max-width: 1023px)').matches) return;
            // the site header is fixed, so stop the preview just below it
            const headerHeight = document.querySelector('header')?.getBoundingClientRect().bottom || 0;
            const top = preview.getBoundingClientRect().top - headerHeight;
            if (top >= 0) return;
            // not while typing: the keyboard is up and the field would scroll away (readonly = PrimeVue dropdown focus)
            if (document.activeElement?.matches('textarea, input:not([type=checkbox]):not([type=radio]):not([readonly])')) return;
            window.scrollTo({ top: window.scrollY + top, behavior: 'smooth' });
        },
        // Captured on demand (add to cart, preview button) instead of after every change.
        async capturePreview() {
            this.isPreviewCapturing = true;
            try {
                this.previewImage = await capturePreview(this.$html2canvas, this.$refs['preview-window']);
                this.storeProduct();
            } catch (error) {
                console.error('Error capturing preview:', error);
            } finally {
                this.isPreviewCapturing = false;
            }
            return this.previewImage;
        },
        // FontFamilySelector hands over its option object, so store a copy.
        setTypo(part, typo) {
            this.selectedOptions = {
                ...this.selectedOptions,
                [part]: { ...this.selectedOptions[part], typo: JSON.parse(JSON.stringify(typo)) },
            };
        },
        setTextSize(key, size) {
            this.$set(this.selectedOptions[key], 'size', size);
        },
        clearOption(option) {
            this.selectedOptions[option].id = null;
            this.selectedOptions[option].preview = null;
        },
        onDropdownShow() {
            this.isDropdownOpen = true;
        },
        onDropdownHide() {
            this.isDropdownOpen = false;
        },
    },
};
